import { useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';

/**
 * useTimelineManagement Hook
 * 
 * Manages timeline adjustments, validation, and role timeline operations.
 * Extracted from StaffingStep for better maintainability.
 */
export const useTimelineManagement = ({
  formData,
  outcomeData,
  updateFormData,
  setIsUpdating,
  migrateRoleToCalendarFormat
}) => {
  // Timeline adjustment state
  const [timelineModalOpen, setTimelineModalOpen] = useState(false);
  const [pendingTimelineChange, setPendingTimelineChange] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [roleTimelineModalOpen, setRoleTimelineModalOpen] = useState(false);
  const [rolesWithoutTimeline, setRolesWithoutTimeline] = useState([]);

  // Detect roles without proper timeline data
  const detectTimelineIssues = useCallback(() => {
    if (!formData?.milestones || formData.milestones.length === 0) {
      return [];
    }

    const issues = [];
    formData.milestones.forEach(milestone => {
      (milestone.roles || []).forEach(role => {
        if (!role.dateRanges || role.dateRanges.length === 0) {
          issues.push({
            roleId: role.id,
            roleName: role.name,
            milestoneName: milestone.name,
            milestoneId: milestone.id
          });
        }
      });
    });

    return issues;
  }, [formData?.milestones]);

  // Update roles without timeline when data changes
  useMemo(() => {
    const issues = detectTimelineIssues();
    setRolesWithoutTimeline(issues);
  }, [detectTimelineIssues]);

  // Handle timeline adjustment confirmation
  const handleTimelineAdjustmentConfirm = useCallback((choice) => {
    if (!pendingTimelineChange) return;

    const { field, value, newMonths, newStartMonth } = pendingTimelineChange;
    
    updateFormData(prev => {
      let updates = {};
      if (field === 'start_month') {
        updates.start_month = value;
        updates.adjustedMonths = newMonths;
      } else if (field === 'adjustedMonths') {
        updates.adjustedMonths = value;
        updates.start_month = newStartMonth;
      }

      if (choice === 'auto-adjust') {
        const milestones = outcomeData?.milestones || [];
        if (milestones.length > 0) {
          updates.milestones = milestones.map(milestone => ({
            ...milestone,
            capex_classification: milestone.capex_classification || 'OPEX',
            roles: []
          }));
        }
      }

      return { ...prev, ...updates };
    });

    setPendingTimelineChange(null);
    setTimelineModalOpen(false);
  }, [pendingTimelineChange, updateFormData, outcomeData?.milestones]);

  // Handle editing role timeline
  const handleEditMilestoneRoleTimeline = useCallback((milestone, role) => {
    // Migrate role to calendar format if needed (lazy migration)
    const migratedRole = migrateRoleToCalendarFormat(role, milestone);

    const milestoneStart = dayjs(milestone.start_date);
    const milestoneEnd = dayjs(milestone.end_date);

    // Use calendar months for milestone context
    const milestoneStartCalendarMonth = milestoneStart.month() + 1;
    const milestoneEndCalendarMonth = milestoneEnd.month() + 1;

    const roleForEditing = {
      ...migratedRole,
      milestone_start_month: milestoneStartCalendarMonth,
      milestone_end_month: milestoneEndCalendarMonth,
      overlap_months: Math.max(1, milestoneEndCalendarMonth - milestoneStartCalendarMonth + 1),
      dateRanges: migratedRole.dateRanges && migratedRole.dateRanges.length > 0 ? migratedRole.dateRanges : [{
        startMonth: milestoneStartCalendarMonth,
        endMonth: milestoneEndCalendarMonth,
        allocation: migratedRole.allocation || 100,
        timelineVersion: '3.0'
      }],
      useDefaultTimeline: migratedRole.useDefaultTimeline !== false,
      milestoneContext: {
        milestoneName: milestone.name,
        milestoneStartMonth: milestoneStartCalendarMonth,
        milestoneEndMonth: milestoneEndCalendarMonth,
        projectStartMonth: formData?.start_month,
        milestoneStartDate: milestone.start_date,
        milestoneEndDate: milestone.end_date
      }
    };

    // If we migrated the role, update it in the form data
    if (migratedRole !== role) {
      updateFormData(prev => ({
        ...prev,
        milestones: prev.milestones.map(m => {
          if (m.id === milestone.id) {
            return {
              ...m,
              roles: m.roles.map(r => r.id === role.id ? migratedRole : r)
            };
          }
          return m;
        })
      }));
    }

    setEditingRole(roleForEditing);
    setRoleTimelineModalOpen(true);
  }, [migrateRoleToCalendarFormat, formData?.start_month, updateFormData]);

  // Handle role timeline update from modal
  const handleRoleTimelineUpdate = useCallback((updatedRole) => {
    setIsUpdating(true);
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === updatedRole.milestone_id) {
          return {
            ...milestone,
            roles: milestone.roles.map(role => {
              if (role.id === updatedRole.id) {
                return {
                  ...role,
                  allocation: Number(updatedRole.allocation) || 0,
                  useDefaultTimeline: updatedRole.useDefaultTimeline,
                  dateRanges: updatedRole.dateRanges || [],
                  overlap_months: Number(updatedRole.overlap_months) || 1,
                  defaultAllocation: Number(updatedRole.defaultAllocation) || Number(updatedRole.allocation) || 100,
                  milestone_start_month: updatedRole.milestone_start_month || role.milestone_start_month,
                  milestone_end_month: updatedRole.milestone_end_month || role.milestone_end_month
                };
              }
              return role;
            })
          };
        }
        return milestone;
      })
    }));
    setTimeout(() => setIsUpdating(false), 500);
  }, [updateFormData, setIsUpdating]);

  // Fix timeline issues
  const handleFixTimelineIssue = useCallback((milestoneId, roleId, field, value) => {
    setIsUpdating(true);
    updateFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map(milestone => {
        if (milestone.id === milestoneId) {
          return {
            ...milestone,
            roles: milestone.roles.map(role => {
              if (role.id === roleId) {
                return { ...role, [field]: value };
              }
              return role;
            })
          };
        }
        return milestone;
      })
    }));
    setTimeout(() => setIsUpdating(false), 500);
  }, [updateFormData, setIsUpdating]);

  return {
    // State
    timelineModalOpen,
    setTimelineModalOpen,
    pendingTimelineChange,
    setPendingTimelineChange,
    editingRole,
    setEditingRole,
    roleTimelineModalOpen,
    setRoleTimelineModalOpen,
    rolesWithoutTimeline,
    setRolesWithoutTimeline,
    
    // Actions
    handleTimelineAdjustmentConfirm,
    handleEditMilestoneRoleTimeline,
    handleRoleTimelineUpdate,
    handleFixTimelineIssue,
    detectTimelineIssues
  };
};